<route>
{
  "meta": {
    "permission": [
      "sales.view_order"
    ]
  }
}
</route>

<template>
  <v-container fluid>
    <v-row row wrap>
      <v-col cols="12" sm="3">
        <i-date-picker
          v-model="start"
          :label="$tc('start_date', 1)"
          color="primary"
        />
      </v-col>
      <v-col cols="12" sm="3">
        <i-date-picker
          v-model="end"
          :label="$t('end_date')"
          color="primary"
          :min="start"
        />
      </v-col>
      <v-col cols="12" sm="3">
        <ValidationProvider
          vid="code"
          :name="$t('suborder_code')"
          rules=""
          v-slot="{ errors }"
        >
          <v-text-field
            v-model="form.code"
            outlined
            :error-messages="errors[0]"
            :label="$t('suborder_code')"
            class="secondary--text"
          ></v-text-field>
        </ValidationProvider>
      </v-col>
      <v-col cols="12" sm="3">
        <ValidationProvider
          vid="name"
          :name="$t('order_name')"
          rules=""
          v-slot="{ errors }"
        >
          <v-text-field
            v-model="form.name"
            outlined
            :error-messages="errors[0]"
            :label="$t('order_name')"
            class="secondary--text"
          ></v-text-field>
        </ValidationProvider>
      </v-col>
      <v-col cols="12" sm="3">
        <ValidationProvider
          vid="status"
          :name="$t('status')"
          rules=""
          v-slot="{ errors }"
        >
          <v-select
            v-model="form.status"
            :items="statusList"
            outlined
            :error-messages="errors[0]"
            :label="$t('status')"
            class="secondary--text"
            item-text="text"
            item-value="value"
            :menu-props="{ maxHeight: '400' }"
          ></v-select>
        </ValidationProvider>
      </v-col>
      <v-col cols="12" sm="3">
        <ValidationProvider
          vid="salesman"
          :name="$tc('salesman', 1)"
          rules=""
          v-slot="{ errors }"
        >
          <v-select
            v-model="form.salesman"
            :items="salesmenList"
            outlined
            :error-messages="errors[0]"
            :label="$tc('salesman', 1)"
            class="secondary--text"
            item-text="fullName"
            item-value="pk"
            :menu-props="{ maxHeight: '400' }"
          ></v-select>
        </ValidationProvider>
      </v-col>
      <v-col cols="12" sm="3">
        <v-btn color="secondary" @click="clear()">
          {{ $t('clear') }}
        </v-btn>
      </v-col>
    </v-row>
    <i-table
      :title="$t('salesReport')"
      :headers="headers"
      app="sales.order"
      api="sale.suborder"
      :opt="{
        company_id: this.company.pk,
        code: this.form.code,
        name: this.form.name,
        status: this.form.status,
        salesman: this.form.salesman,
        date_from: this.form.date_from,
        date_to: this.form.date_to
      }"
      dontCreate
      dontRemove
      dontEdit
      dontSearch
    >
      <template v-slot:item.orderDate="{ item }">
        <span v-text="dateFormat(item.orderDate)"></span>
      </template>
      <template v-slot:item.promiseDate="{ item }">
        <span v-text="dateFormat(item.promiseDate)"></span>
      </template>
      <template v-slot:item.price="{ item }">
        <i-money :value="item.price" mode="text"></i-money>
      </template>
      <template v-slot:item.subtotal="{ item }">
        <i-money :value="item.subtotal" mode="text"></i-money>
      </template>
      <template v-slot:extraToolbar>
        <v-divider class="mx-4" inset vertical></v-divider>
        <i-btn
          text
          :small="$vuetify.breakpoint.smAndDown"
          icon="fa-file-excel"
          :title="$t('download', { value: $t('salesReport') })"
          color="text-dark"
          @click="getSaleOrdersExcel()"
        >
        </i-btn>
        <i-btn
          text
          :small="$vuetify.breakpoint.smAndDown"
          icon="fa-file-pdf"
          :title="$t('download', { value: $t('salesReport') })"
          color="text-dark"
          @click="getSaleOrdersPDF()"
        >
        </i-btn>
      </template>
    </i-table>
  </v-container>
</template>
<script>
import { mapGetters } from 'vuex'

export default {
  data() {
    return {
      start: '',
      end: '',
      form: {
        name: '',
        code: '',
        status: '',
        salesman: '',
        date_from: '',
        date_to: ''
      },
      salesmen: [],
      statusList: [
        { text: this.$t('all'), value: '' },
        { text: this.$t('draft'), value: 'draft' },
        { text: this.$t('quote'), value: 'quote' },
        { text: this.$t('sended_quote'), value: 'sended_quote' },
        { text: this.$t('correction'), value: 'correction' },
        { text: this.$t('in_process'), value: 'in_process' },
        { text: this.$t('production_ready'), value: 'production_ready' },
        { text: this.$t('delivery'), value: 'delivery' },
        { text: this.$t('finished'), value: 'finished' },
        { text: this.$t('canceled'), value: 'canceled' }
      ]
    }
  },
  async mounted() {
    try {
      const response = await this.$api.sale.availableusers({
        opt: {
          params: {
            role: 'Salesman'
          }
        }
      })
      this.salesmen = response.data.results || response.data || []
    } catch (e) {
      this.salesmen = []
    }
  },
  computed: {
    salesmenList() {
      const items = (this.salesmen || []).map((s) => ({
        pk: s.pk || s.id,
        fullName:
          `${s.first_name || ''} ${s.last_name || ''}`.trim() || s.username
      }))
      return [{ pk: '', fullName: this.$t('all') }, ...items]
    },
    headers() {
      return [
        {
          text: this.$t('suborder_code'),
          value: 'code'
        },
        {
          text: this.$t('order_name'),
          value: 'projectName'
        },
        {
          text: this.$t('order_status'),
          value: 'orderMain'
        },
        {
          text: this.$tc('customer', 1),
          value: 'clientName'
        },
        {
          text: this.$t('dateOrder'),
          value: 'orderDate'
        },
        {
          text: this.$t('promise_date'),
          value: 'promiseDate'
        },
        {
          text: this.$tc('salesman', 1),
          value: 'salesmanName'
        },
        {
          text: this.$tc('product', 1),
          value: 'own_prod'
        },
        {
          text: this.$tc('price', 1),
          value: 'price',
          align: 'right'
        },
        {
          text: this.$tc('quantity', 1),
          value: 'quantity',
          align: 'right'
        },
        {
          text: this.$tc('subtotal', 1),
          value: 'subtotal',
          align: 'right'
        }
      ]
    },
    ...mapGetters({
      company: 'company/getCompanyData'
    })
  },
  methods: {
    getSaleOrdersExcel() {
      this.downloadFile({
        api: 'sale.suborder',
        method: 'excel',
        filename: 'SaleOrders',
        action: 'download',
        ext: 'xlsx',
        opt: {
          pk: this.company.pk,
          params:
            '?name=' +
            this.form.name +
            '&code=' +
            this.form.code +
            '&status=' +
            this.form.status +
            '&salesman=' +
            (this.form.salesman || '') +
            '&date_from=' +
            this.start +
            '&date_to=' +
            this.end
        }
      })
    },
    getSaleOrdersPDF() {
      this.downloadFile({
        api: 'sale.suborder',
        method: 'pdf',
        filename: 'SaleOrders',
        action: 'download',
        ext: 'pdf',
        opt: {
          pk: this.company.pk,
          params:
            '?name=' +
            this.form.name +
            '&code=' +
            this.form.code +
            '&status=' +
            this.form.status +
            '&salesman=' +
            (this.form.salesman || '') +
            '&date_from=' +
            this.start +
            '&date_to=' +
            this.end
        }
      })
    },
    clear() {
      this.form = {
        name: '',
        code: '',
        status: '',
        salesman: '',
        date_from: '',
        date_to: ''
      }
      this.start = ''
      this.end = ''
    }
  },
  watch: {
    start: {
      handler(val) {
        if (val) {
          this.form.date_from = this.dateFormat(val)
        }
      }
    },
    end: {
      handler(val) {
        if (val) {
          this.form.date_to = this.dateFormat(val)
        }
      }
    }
  }
}
</script>
